// Phase 2.2 Lite — Request changes modal for the public review page.
// Used by ReviewHeader when the reviewer wants to send feedback instead
// of approving. Anonymous — token-only access.

import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, MessageSquare } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_NOTE = 2000;

export default function RequestChangesModal({ eventSlug, token, onClose, onSubmitted }) {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!note.trim()) {
      toast.error('Please describe what should be changed');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/public/review/${eventSlug}/request-changes`,
        { token, note: note.trim().slice(0, MAX_NOTE) },
      );
      toast.success('Changes requested');
      onSubmitted?.();
      onClose();
    } catch {
      toast.error('Could not send request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="request-changes-modal"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3 flex items-start justify-between border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-amber-500" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Request changes
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600"
            data-testid="request-changes-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Describe what you&apos;d like updated. The designer will see your note in their Studio.
          </p>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, MAX_NOTE))}
            rows={5}
            placeholder="e.g., Update the venue wording on the second card, lighten the background…"
            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 resize-none focus:border-amber-400 focus:ring-1 focus:ring-amber-300 outline-none"
            data-testid="request-changes-textarea"
          />
          <div className="text-right text-[10px] text-gray-400">
            {note.length}/{MAX_NOTE}
          </div>
        </div>
        <div className="px-5 py-3.5 border-t border-gray-100 dark:border-gray-800 flex items-center justify-end gap-2 bg-gray-50/50 dark:bg-gray-900/50">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3.5 py-1.5 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            data-testid="request-changes-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !note.trim()}
            className="px-4 py-1.5 rounded-lg text-xs font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1.5"
            data-testid="request-changes-submit-btn"
          >
            {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageSquare className="w-3 h-3" />}
            Send request
          </button>
        </div>
      </div>
    </div>
  );
}
